<template>
  <div class="vsh-wrapper" :style="maxVisible ? { maxWidth: (maxVisible * itemWidth + 80) + 'px' } : {}">
    <div v-if="canScrollLeft" class="vsh-arrow"
         @mousedown="startScroll(-1)" @mouseup="stopScroll" @mouseleave="stopScroll"
         @touchstart.prevent="startScroll(-1)" @touchend="stopScroll">
      <i class="fad fa-angle-left fs-2x"></i>
    </div>

    <div ref="scroller" class="vsh-scroller" @scroll="onScroll">
      <div class="vsh-spacer" :style="{ minWidth: startIndex * itemWidth + 'px' }" v-if="startIndex > 0"></div>
      <div v-for="(item, i) in visibleItems" :key="startIndex + i" class="vsh-item" :style="{ minWidth: itemWidth + 'px' }">
        <slot :child="item" :index="startIndex + i" />
      </div>
      <div class="vsh-spacer" :style="{ minWidth: (items.length - endIndex) * itemWidth + 'px' }" v-if="endIndex < items.length"></div>
    </div>

    <div v-if="canScrollRight" class="vsh-arrow"
         @mousedown="startScroll(1)" @mouseup="stopScroll" @mouseleave="stopScroll"
         @touchstart.prevent="startScroll(1)" @touchend="stopScroll">
      <i class="fad fa-angle-right fs-2x"></i>
    </div>

    <div v-if="items.length > effectiveVisible" class="vsh-counter">
      {{ items.length }}
    </div>
  </div>
</template>

<script lang="ts">
import {Vue, Component, Prop} from 'vue-property-decorator';

@Component
export default class VirtualScrollHorizontal extends Vue {
  @Prop({type: Array, required: true}) readonly items!: any[];
  @Prop({type: Number, default: 140}) readonly itemWidth!: number;
  @Prop({type: Number, default: 0}) readonly maxVisible!: number;
  @Prop({type: Number, default: 2}) readonly overscan!: number;

  canScrollLeft = false
  canScrollRight = false
  scrollTimer: any = null
  holdStart = 0
  scrollLeft = 0

  get effectiveVisible(): number {
    if (this.maxVisible > 0) return this.maxVisible
    return this.items.length
  }

  get startIndex(): number {
    return Math.max(0, Math.floor(this.scrollLeft / this.itemWidth) - this.overscan)
  }

  get endIndex(): number {
    const visible = this.maxVisible > 0 ? this.maxVisible : Math.ceil(800 / this.itemWidth)
    return Math.min(this.items.length, Math.ceil(this.scrollLeft / this.itemWidth) + visible + this.overscan)
  }

  get visibleItems(): any[] {
    return this.items.slice(this.startIndex, this.endIndex)
  }

  startScroll(direction: number) {
    this.holdStart = Date.now()
    this.doScroll(direction)
    this.scrollTimer = setInterval(() => {
      this.doScroll(direction)
    }, 120)
  }

  doScroll(direction: number) {
    const el = this.$refs.scroller as HTMLElement
    if (!el) return
    const held = Date.now() - this.holdStart
    const speed = held > 600 ? this.itemWidth * 2 : this.itemWidth
    el.scrollBy({left: direction * speed, behavior: 'smooth'})
  }

  stopScroll() {
    if (this.scrollTimer) {
      clearInterval(this.scrollTimer)
      this.scrollTimer = null
    }
  }

  onScroll() {
    const el = this.$refs.scroller as HTMLElement
    if (!el) return
    this.scrollLeft = el.scrollLeft
    this.updateArrows()
    this.$emit('scroll')
  }

  updateArrows() {
    const el = this.$refs.scroller as HTMLElement
    if (!el) return
    this.canScrollLeft = el.scrollLeft > 2
    this.canScrollRight = el.scrollLeft < el.scrollWidth - el.clientWidth - 2
  }

  mounted() {
    this.$nextTick(() => this.updateArrows())
  }

  updated() {
    this.$nextTick(() => this.updateArrows())
  }

  beforeDestroy() {
    this.stopScroll()
  }
}
</script>

<style scoped>
.vsh-wrapper {
  display: flex;
  align-items: flex-start;
  position: relative;
}

.vsh-scroller {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  overflow-x: auto;
  overflow-y: visible;
  flex: 1;
  min-width: 0;
  scrollbar-width: none;
  -ms-overflow-style: none;
  scroll-snap-type: x mandatory;
}

.vsh-scroller::-webkit-scrollbar {
  display: none;
}

.vsh-item {
  scroll-snap-align: start;
  flex-shrink: 0;
}

.vsh-spacer {
  flex-shrink: 0;
  pointer-events: none;
}

.vsh-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px dashed #b5b5c3;
  background: white;
  cursor: pointer;
  user-select: none;
  flex-shrink: 0;
  z-index: 2;
  margin-top: 30px;
  transition: background 0.15s;
}

.vsh-arrow:hover {
  background: #f1faff;
  border-color: var(--bs-primary, #009ef7);
}

.vsh-arrow:active {
  background: #d4edff;
}

.vsh-counter {
  position: absolute;
  top: -8px;
  right: 0;
  font-size: 10px;
  font-weight: 700;
  background: #f1416c;
  color: white;
  padding: 0 6px;
  border-radius: 8px;
  pointer-events: none;
}
</style>
